/***
*
*   ADMIN
*   System administration — user management, health monitoring, and configuration
*
**********/

import { useState, useContext, useEffect } from 'react';
import { Animate, useAPI, ViewContext, AuthContext } from 'components/lib';
import { buildWebappUrl } from '../utility/webapp';

// ---------------------------------------------------------------------------
// Shared card styles
// ---------------------------------------------------------------------------

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 24,
};

const placeholderCard = {
  background: '#f9fafb',
  border: '1px dashed #d1d5db',
  borderRadius: 12,
  padding: 24,
};

const sectionHeader = {
  fontSize: 18,
  fontWeight: 700,
  color: '#111827',
  marginBottom: 16,
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  const isOk = status === 'ok';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: isOk ? '#dcfce7' : '#fee2e2',
      color: isOk ? '#16a34a' : '#dc2626',
      borderRadius: 6,
      padding: '3px 10px',
      fontSize: 13,
      fontWeight: 500,
    }}>
      <span style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: isOk ? '#16a34a' : '#dc2626',
        display: 'inline-block',
      }} />
      {isOk ? 'Operational' : 'Error'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// User Management — Internal Team table
// ---------------------------------------------------------------------------

function InternalTeamCard() {
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const users = useAPI('/api/user');
  const [openKebab, setOpenKebab] = useState(null);

  // Close kebab on outside click
  useEffect(() => {
    if (openKebab === null) return;
    function handler() { setOpenKebab(null); }
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openKebab]);

  const masterUsers = (users.data || []).filter(u => u.permission === 'master');

  function formatLastActive(dateStr) {
    if (!dateStr) return '—';
    return String(dateStr).split(' ')[0] || '—';
  }

  function deleteUser(u) {
    viewContext.modal.show({
      title: 'Remove Internal User',
      form: {},
      buttonText: 'Remove User',
      text: `Are you sure you want to remove ${u.email} from the internal team?`,
      url: buildWebappUrl(authContext.remote_server, `/api/user/${u.id}`),
      method: 'DELETE',
      destructive: true,
      audit: { action: 'user.deleted', target_type: 'user', target_id: u.id },
    }, () => {
      viewContext.notification.show(`${u.email} was removed`, 'success', true);
      users.refresh?.();
    });
  }

  const menuItemStyle = {
    display: 'block',
    width: '100%',
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontSize: 14,
    color: '#374151',
    cursor: 'pointer',
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
        Internal Team
      </div>

      {users.loading ? (
        <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading...</p>
      ) : masterUsers.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 14 }}>No internal users</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                {['Name', 'Email', 'Last Active', 'Actions'].map(col => (
                  <th key={col} style={{
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 13,
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                    background: '#fff',
                  }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {masterUsers.map(u => (
                <tr
                  key={u.id}
                  style={{ borderBottom: '1px solid #f9fafb' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '0 16px', height: 52, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>
                    {u.name || '—'}
                  </td>
                  <td style={{ padding: '0 16px', height: 52, color: '#374151' }}>
                    {u.email || '—'}
                  </td>
                  <td style={{ padding: '0 16px', height: 52, color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {formatLastActive(u.date_last_active || u.date_created)}
                  </td>
                  <td
                    style={{ padding: '0 16px', height: 52, position: 'relative' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={e => { e.stopPropagation(); setOpenKebab(openKebab === u.id ? null : u.id); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        fontSize: 20,
                        lineHeight: 1,
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      ⋮
                    </button>
                    {openKebab === u.id && (
                      <div
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          zIndex: 100,
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          minWidth: 140,
                        }}
                      >
                        <button
                          onClick={() => { deleteUser(u); setOpenKebab(null); }}
                          style={{ ...menuItemStyle, color: '#dc2626' }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// System Health — 3-column status grid
// ---------------------------------------------------------------------------

const STATUS_ITEMS = [
  {
    label: 'API Status',
    status: 'ok',
    todo: '// TODO: wire to health monitoring',
  },
  {
    label: 'Database',
    status: 'ok',
    todo: '// TODO: wire db health check',
  },
  {
    label: 'Last Deploy',
    value: '—',
    todo: '// TODO: wire deployment tracking',
  },
];

function SystemHealthSection() {
  return (
    <div>
      <div style={sectionHeader}>System Health</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {STATUS_ITEMS.map(item => (
          <div
            key={item.label}
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderLeft: `4px solid ${item.status === 'ok' ? '#16a34a' : item.status === 'error' ? '#dc2626' : '#d1d5db'}`,
              borderRadius: 12,
              padding: 20,
            }}
          >
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{item.label}</div>
            {item.status ? (
              <StatusBadge status={item.status} />
            ) : (
              <span style={{ fontSize: 20, fontWeight: 600, color: '#111827' }}>{item.value}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

function ConfigRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 14, color: '#6b7280', fontFamily: 'monospace', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
        {value}
      </span>
    </div>
  );
}

function ConfigurationSection() {
  const remoteUrl = process.env.REACT_APP_REMOTE_URL || '—';
  const env = process.env.NODE_ENV;

  return (
    <div>
      <div style={sectionHeader}>Configuration</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Live config values */}
        <div style={card}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 12 }}>
            Environment
          </div>
          <ConfigRow label="Remote App URL" value={remoteUrl} />
          <ConfigRow label="Environment" value={env} />
        </div>

        {/* Feature Flags placeholder */}
        <div style={placeholderCard}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
            Feature Flags
          </div>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
            {/* TODO: LaunchDarkly integration */}
            LaunchDarkly integration not yet configured.
          </p>
        </div>

        {/* Email Templates placeholder */}
        <div style={placeholderCard}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
            Email Templates
          </div>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
            {/* TODO */}
            Email template management coming soon.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function Admin() {
  return (
    <Animate type="pop">
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Admin</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>
          Manage internal team access, monitor system health, and configure application settings.
        </p>
      </div>

      {/* User Management */}
      <div style={{ marginBottom: 36 }}>
        <div style={sectionHeader}>User Management</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <InternalTeamCard />

          {/* Pending Invites placeholder */}
          <div style={placeholderCard}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
              Pending Invites
            </div>
            <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
              {/* TODO: invite management API */}
              Invite management coming soon.
            </p>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div style={{ marginBottom: 36 }}>
        <SystemHealthSection />
      </div>

      {/* Configuration */}
      <ConfigurationSection />
    </Animate>
  );
}
